<template>
  <div class="dynamic-list">
    <div class="list-container">
      <div v-if="loading" class="loading">
        <i class="el-icon-loading"></i>
        <span>加载中...</span>
      </div>
      <div v-else-if="list.length === 0" class="empty-list">
        <p>暂无内容</p>
      </div>
      <div v-else class="list-items">
        <div v-for="item in list" :key="item.id" class="list-item" @click="handleItemClick(item)">
          <div class="item-image">
            <img v-if="getItemImage(item)" :src="getItemImage(item)" alt="" @error="handleImageError">
            <div v-else class="image-placeholder">
              <i class="el-icon-picture"></i>
            </div>
          </div>
          <div class="item-content">
            <h3 class="item-title">{{ getItemTitle(item) }}</h3>
            <p class="item-summary">{{ getItemSummary(item) }}</p>
            <div class="item-footer">
              <span class="item-date">{{ getItemDate(item) }}</span>
              <span class="item-link">
                查看详情
                <i class="el-icon-arrow-right"></i>
              </span>
            </div>
          </div>
        </div>
      </div>
      <!-- 分页 -->
      <el-pagination v-if="total > pageSize" :current-page="pageNo" :page-size="pageSize" :total="total" layout="prev, pager, next" @current-change="handlePageChange" class="pagination" />
    </div>
  </div>
</template>

<script>
import { queryDynamicHtmlList } from '@/api/menu'

export default {
  name: 'DynamicList',
  props: {
    menuCode: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: '88'
    },
    status: {
      type: Number,
      default: 2
    },
    pageSize: {
      type: Number,
      default: 10
    }
  },
  data () {
    return {
      list: [],
      loading: false,
      pageNo: 1,
      total: 0
    }
  },
  watch: {
    menuCode () {
      this.loadList()
    }
  },
  mounted () {
    this.loadList()
  },
  methods: {
    async loadList () {
      if (!this.menuCode) {
        return
      }
      this.loading = true
      try {
        const res = await queryDynamicHtmlList({
          webCode: this.menuCode,
          type: this.type,
          pageNo: this.pageNo,
          pageSize: this.pageSize,
          status: this.status
        })
        if (res && res.result) {
          this.list = res.result.records || []
          this.total = res.result.total || 0
        }
      } catch (error) {
        console.error('加载列表失败:', error)
        this.list = []
      } finally {
        this.loading = false
      }
    },
    handlePageChange (page) {
      this.pageNo = page
      this.loadList()
      // 滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    // 解析 htmlContent 中的 JSON 数据
    parseHtmlContent (htmlContent) {
      if (!htmlContent) return null
      try {
        // 尝试解析 JSON
        return JSON.parse(htmlContent)
      } catch (e) {
        // 如果不是 JSON，返回 null
        return null
      }
    },
    getItemImage (item) {
      // 优先从解析后的 htmlContent JSON 中获取 image
      const parsedContent = this.parseHtmlContent(item.htmlContent)
      if (parsedContent && parsedContent.image) {
        return parsedContent.image
      }
      // 其次使用 icon 字段
      if (item.icon) {
        return item.icon
      }
      // 最后尝试从 HTML 中提取第一张图片
      if (item.htmlContent) {
        const imgMatch = item.htmlContent.match(/<img[^>]+src=["']([^"']+)["']/i)
        if (imgMatch && imgMatch[1]) {
          return imgMatch[1]
        }
      }
      return null
    },
    getItemTitle (item) {
      // 优先从解析后的 htmlContent JSON 中获取 title
      const parsedContent = this.parseHtmlContent(item.htmlContent)
      if (parsedContent && parsedContent.title) {
        return parsedContent.title
      }
      // 其次使用 item.title
      return item.title || '无标题'
    },
    getItemSummary (item) {
      // 优先从解析后的 htmlContent JSON 中获取 summary
      const parsedContent = this.parseHtmlContent(item.htmlContent)
      if (parsedContent && parsedContent.summary) {
        return parsedContent.summary
      }
      // 其次从 content 中提取纯文本
      if (parsedContent && parsedContent.content) {
        const text = parsedContent.content.replace(/<[^>]+>/g, '').trim()
        return text.length > 150 ? text.substring(0, 150) + '...' : text
      }
      // 最后从 htmlContent 中提取纯文本
      if (item.htmlContent) {
        const text = item.htmlContent.replace(/<[^>]+>/g, '').trim()
        return text.length > 150 ? text.substring(0, 150) + '...' : text
      }
      return item.remark || '暂无摘要'
    },
    getItemDate (item) {
      // 优先从解析后的 htmlContent JSON 中获取 publishTime
      const parsedContent = this.parseHtmlContent(item.htmlContent)
      if (parsedContent && parsedContent.publishTime) {
        return this.formatDate(parsedContent.publishTime)
      }
      // 其次使用 createTime 或 updateTime
      return this.formatDate(item.createTime || item.updateTime)
    },
    formatDate (dateStr) {
      if (!dateStr) return ''
      // 如果是时间戳，转换为日期对象
      let date
      if (typeof dateStr === 'number') {
        date = new Date(dateStr)
      } else if (typeof dateStr === 'string') {
        // 尝试解析字符串日期
        date = new Date(dateStr)
      } else {
        return ''
      }
      // 检查日期是否有效
      if (isNaN(date.getTime())) {
        return ''
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    handleItemClick (item) {
      // 跳转到详情页，使用动态ID
      if (item.id) {
        this.$router.push({
          path: `/menu/${this.menuCode}/detail/${item.id}`
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error('路由跳转失败:', err)
          }
        })
      }
    },
    handleImageError (event) {
      // 图片加载失败时隐藏
      event.target.style.display = 'none'
    }
  }
}
</script>

<style scoped>
.dynamic-list {
  width: 100%;
  background: #fff;
}

.list-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #666;
  gap: 12px;
}

.empty-list {
  text-align: center;
  padding: 60px 0;
  color: #999;
}

.list-items {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.list-item {
  display: flex;
  gap: 24px;
  padding: 20px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  background: #fff;
}

.list-item:hover {
  border-color: #52c41a;
  box-shadow: 0 2px 8px rgba(82, 196, 26, 0.15);
  transform: translateY(-2px);
}

.item-image {
  flex-shrink: 0;
  width: 320px;
  height: 200px;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f5f5;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ccc;
  font-size: 48px;
}

.item-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.item-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.item-summary {
  font-size: 14px;
  color: #666;
  line-height: 1.8;
  margin: 0 0 16px 0;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.item-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.item-date {
  font-size: 13px;
  color: #999;
}

.item-link {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: #52c41a;
  transition: all 0.2s;
}

.item-link:hover {
  color: #389e0d;
}

.item-link i {
  font-size: 12px;
}

.pagination {
  margin-top: 40px;
  display: flex;
  justify-content: center;
}

/* 移动端 */
@media (max-width: 768px) {
  .list-container {
    padding: 24px 16px;
  }

  .list-item {
    flex-direction: column;
    gap: 16px;
    padding: 16px;
  }

  .item-image {
    width: 100%;
    height: 200px;
  }

  .item-title {
    font-size: 18px;
  }

  .item-summary {
    font-size: 13px;
    -webkit-line-clamp: 2;
  }

  .item-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>
